/**
 * Πηγή ιστορικού MRO: το live API epitokia.mysolon.gr (δεδομένα Τράπεζας της
 * Ελλάδος, ανανέωση καθημερινά) με εφεδρεία το ενσωματωμένο DEFAULT_MRO_HISTORY.
 */

import { DEFAULT_MRO_HISTORY, type MroEntry } from "./calculator";

export const MRO_API_URL = "https://epitokia.mysolon.gr/ecb/mro";
const FETCH_TIMEOUT_MS = 5000;

export interface MroHistoryResult {
  history: MroEntry[];
  source: "api" | "bundled";
  fetchedAt?: string;
}

const BUNDLED_RESULT: MroHistoryResult = {
  history: DEFAULT_MRO_HISTORY,
  source: "bundled",
};

/**
 * Αυστηρός έλεγχος του API payload: εργαλείο νομικών υπολογισμών δεν πρέπει ποτέ
 * να τρέξει με ελλιπή ή παραμορφωμένα δεδομένα — σε κάθε αμφιβολία επιστρέφεται
 * το ενσωματωμένο ιστορικό.
 */
export function parseMroApiPayload(payload: unknown): MroEntry[] | null {
  if (typeof payload !== "object" || payload === null) return null;
  const rates = (payload as { rates?: unknown }).rates;
  if (!Array.isArray(rates) || rates.length === 0) return null;

  const entries: MroEntry[] = [];
  for (const item of rates) {
    if (typeof item !== "object" || item === null) return null;
    const { date, rate } = item as { date?: unknown; rate?: unknown };
    if (typeof date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
    if (typeof rate !== "number" || !Number.isFinite(rate) || rate < -1 || rate > 30) return null;
    entries.push({ date, rate });
  }

  entries.sort((a, b) => a.date.localeCompare(b.date));

  // Λογικοί έλεγχοι πληρότητας: το ιστορικό ξεκινά 01/01/1999 και δεν μπορεί να
  // είναι αισθητά μικρότερο από το ενσωματωμένο.
  if (entries[0]!.date !== "1999-01-01") return null;
  if (entries.length < DEFAULT_MRO_HISTORY.length - 2) return null;
  const lastBundled = DEFAULT_MRO_HISTORY[DEFAULT_MRO_HISTORY.length - 1]!.date;
  if (entries[entries.length - 1]!.date < lastBundled) return null;

  return entries;
}

/** Φέρνει το ιστορικό MRO από το API· σε οποιαδήποτε αποτυχία επιστρέφει το ενσωματωμένο. */
export async function fetchMroHistory(
  fetchImpl: typeof fetch = fetch
): Promise<MroHistoryResult> {
  try {
    const response = await fetchImpl(MRO_API_URL, {
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return BUNDLED_RESULT;

    const payload: unknown = await response.json();
    const history = parseMroApiPayload(payload);
    if (history === null) return BUNDLED_RESULT;

    const fetchedAt = (payload as { fetchedAt?: unknown }).fetchedAt;
    return {
      history,
      source: "api",
      ...(typeof fetchedAt === "string" ? { fetchedAt } : {}),
    };
  } catch {
    return BUNDLED_RESULT;
  }
}
